<template>
  <div class="header-container">
    <Logo />
    <LanguageDropdown :languageList="languageList" :defaultLang="userLang" @language-changed="handleLanguageChange" />
  </div>

  <div class="success-container inner">
    <div class="account-manage">
      <div v-if="loading" class="loader-container">
        <div class="simple-spinner"></div>
      </div>

      <div v-else>
        <h1 class="password_heading_text_success">
          {{ translatedHeading }}
        </h1>

        <article>
          <!-- ✅ Custom success icon -->
          <div class="success-icon"></div>
          <div class="text"></div>
        </article>

        <p class="introduction">
          {{ translatedMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import LanguageDropdown from "./LanguageDropdown.vue";
import Logo from "./logo.vue";

const languageList = ref<any[]>([]);
const userLang = ref(localStorage.getItem("userLang") || "EN");
const headingObj = ref<any>(null);
const messageObj = ref<any>(null);
const loading = ref(true);

// Computed: Heading translation
const translatedHeading = computed(() => {
  if (!headingObj.value) return "";

  const selectedLang = languageList.value.find(lang => lang.key === userLang.value);
  const selectedLangId = selectedLang?._id;

  const translation = headingObj.value.translate.find(
    (t: any) => t.languageId === selectedLangId && t.isPublished
  );

  return translation?.value || headingObj.value.base;
});

// Computed: Message translation
const translatedMessage = computed(() => {
  if (!messageObj.value) return "";

  const selectedLang = languageList.value.find(lang => lang.key === userLang.value);
  const selectedLangId = selectedLang?._id;

  let translation = messageObj.value.translate.find(
    (t: any) => t.languageId === selectedLangId && t.isPublished
  )?.value;

  if (!translation) translation = messageObj.value.base;

  const identifier = localStorage.getItem("email_for_reset") || "";
  if (identifier) {
    translation = translation.replace(/%this\.user\.identifier%/g, identifier);
  }

  return translation;
});

// Fetch language and translation data
const fetchData = async () => {
  loading.value = true;

  try {
    const translateText = localStorage.getItem("translate_text");
    const successRes = localStorage.getItem("success_res");
    const emailForReset = localStorage.getItem("email_for_reset");

    if (!successRes || !emailForReset) {
      loading.value = false;
      return;
    }

    if (translateText) {
      const parsedData = JSON.parse(translateText);

      languageList.value = parsedData.languageList || [];
      headingObj.value = parsedData.updated_heading_message_text || null;
      messageObj.value = parsedData.updated_message_text || null;
    }
  } catch (err) {
    console.error("Error fetching success page data:", err);
  } finally {
    loading.value = false;
  }
};

// Handle language change
const handleLanguageChange = (lang: string) => {
  userLang.value = lang;

  const storedData = localStorage.getItem("translate_text");
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      parsedData.userLang = lang; // update userLang in storage
      localStorage.setItem("translate_text", JSON.stringify(parsedData));
    } catch (err) {
      console.error("Failed to update language in localStorage:", err);
    }
  }
};

// Initialize on mount
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
#app {
  width: 100%;
  margin: 0 auto;
  z-index: 20;
  font-size: 16px;

  p,
  .p-inputtext,
  .p-button {
    font-size: 1em;
  }

  .inner {
    position: relative;
    margin: 0 auto;
    width: 100%;
    max-width: 45em;
  }
}

/* Buttons */
.buttonSubmit {
  background-color: #44142a !important;
  border: #44142a !important;
  color: #fff !important;
  margin-top: 25px;
  cursor: pointer;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
}

.buttonSubmit:focus {
  outline: none;
  box-shadow: 0 0 0 3px #44142a !important;
  background-color: #44142a !important;
}

/* Password input */
.password-field {
  height: 50px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 16px;
}

.password-field:focus {
  border: 1px solid #44142a !important;
  box-shadow: 0 0 0 1px #44142a !important;
  outline: none;
}

/* Loader Styles */
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.simple-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #ccc;
  border-top-color: #44142a;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-container {
  display: flex;
  flex-direction: column;
  padding: 50px 20px;
  text-align: center;
}

/* Success Icon Styling - FIXED */
.success-container .pi-check-circle[data-v-76f48ab4] {
  color: #23d160;
  font-size: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 68px;
  height: 68px;
  border: 2px solid #23d160;
  border-radius: 50%;
  /* margin: 20px auto; */
  position: relative;
  margin-bottom: 24px;
}

.success-container .pi-check-circle[data-v-76f48ab4]::before {
  content: "✓";
  font-size: 37px;
  color: #23d160;
  display: block;
  position: absolute;
}


/* Success Icon (HTML + CSS only) */
.success-icon {
  width: 68px;
  height: 68px;
  border: 2px solid #23d160;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
}

.success-icon::before {
  content: "✓";
  font-size: 37px;
  color: #23d160;
  line-height: 1;
}


.password_heading_text_success {
  margin: 20px 0 56px;
  letter-spacing: 0.4px;
  line-height: 60px;
  font-style: normal;
  font-weight: 700;
  font-family: 'Fira Sans', sans-serif;
  color: #1d1d1d;
  text-align: start;
}

.introduction {
  margin-bottom: 20px;
  color: #555;
  font-size: 16px;
  text-align: start;
}

/* Header */
.header-container {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #e0dbdb;
  padding: 0.9rem 1rem;
  background-color: #fff;
}

/* Dropdown margin fix */
.dropdown {
  margin-right: 20px;
}

/* Responsive Fixes */
@media (max-width: 576px) {
  .header-container {
    flex-direction: column;
    column-gap: 10px;
  }

  .dropdown {
    margin-right: 0;
  }
}

.account-manage {
  max-width: 53rem;
  margin: 0 auto;
  width: 100%;
}
</style>
